import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Clock } from 'lucide-react';
import { useGetEtudiantsQuery } from '../../services/etudiantsApi';
import { useSaisieIndividuelleMutation } from '../../services/notesDevoirsApi';
import { typeFormationForClasse } from '../../utils/typeFormation';
import HistoriqueSaisieModal from './HistoriqueSaisieModal';

interface SaisieIndividuelleProps {
  matiereId: number;
  matiereLabel?: string;
  anneeAcademique: string;
  semestre: 'S1' | 'S2';
  classe: string;
  onSuccess?: () => void;
}

const SaisieIndividuelle: React.FC<SaisieIndividuelleProps> = ({
  matiereId,
  matiereLabel,
  anneeAcademique,
  semestre,
  classe,
  onSuccess,
}) => {
  // Filtrer les étudiants par classe ; CS si la classe finit par "-CS", sinon FI+DD.
  const { data: etudiants, isLoading: loadingEtudiants } = useGetEtudiantsQuery({ classe, type_formation: typeFormationForClasse(classe) });
  const [saisieIndividuelle, { isLoading: isSaving }] = useSaisieIndividuelleMutation();

  const [etudiantId, setEtudiantId] = useState<number | null>(null);
  // Fix 2026-08-27 : plus de cases absent — si le champ est vide au blur, note = 0.
  const [note1, setNote1] = useState<string>('');
  const [note2, setNote2] = useState<string>('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showHistorique, setShowHistorique] = useState(false);

  const note1Ref = useRef<HTMLInputElement>(null);

  const currentIndex = useMemo(() => {
    if (!etudiantId || !etudiants) return -1;
    return etudiants.findIndex(e => e.id === etudiantId);
  }, [etudiantId, etudiants]);

  // Avertissement si saisie en cours non sauvegardée
  useEffect(() => {
    const hasUnsaved = etudiantId !== null && (!!note1 || !!note2);
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [etudiantId, note1, note2]);

  // Calculer la moyenne en temps réel (vide = 0)
  const calculerMoyenne = (): number | null => {
    const n1 = note1.trim() === '' ? 0 : parseFloat(note1);
    const n2 = note2.trim() === '' ? 0 : parseFloat(note2);
    if (!isNaN(n1) && !isNaN(n2)) return (n1 + n2) / 2;
    return null;
  };
  const moyenne = calculerMoyenne();

  // Soumet la fiche + advance to next
  const submitWith = useCallback(async (n1: string, n2: string) => {
    setMessage(null);
    if (!etudiantId) {
      setMessage({ type: 'error', text: 'Veuillez sélectionner un étudiant' });
      return;
    }
    try {
      // Vide → 0 (fix 2026-08-27 : plus de distinction absent/0)
      const n1Val = n1.trim() === '' ? 0 : parseFloat(n1);
      const n2Val = n2.trim() === '' ? 0 : parseFloat(n2);
      const result = await saisieIndividuelle({
        etudiant_id: etudiantId,
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre: semestre,
        note1: n1Val,
        absent1: false,
        note2: n2Val,
        absent2: false,
      }).unwrap();

      if (result.success) {
        setMessage({
          type: 'success',
          text: `Note ${result.created ? 'créée' : 'modifiée'} ! Moyenne : ${result.moyenne_devoirs}`,
        });
        const nextEtu = etudiants && currentIndex >= 0 && currentIndex < etudiants.length - 1
          ? etudiants[currentIndex + 1]
          : null;
        setNote1(''); setNote2('');
        if (nextEtu) {
          setEtudiantId(nextEtu.id);
          setTimeout(() => { note1Ref.current?.focus(); }, 50);
        } else {
          setEtudiantId(null);
        }
        onSuccess?.();
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.error || 'Erreur lors de la sauvegarde' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etudiantId, matiereId, anneeAcademique, semestre, etudiants, currentIndex, onSuccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    submitWith(note1, note2);
  };

  // Raccourci C/Espace dans D1/D2 → copie D1→D2 + valide + étudiant suivant
  const handleNoteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'c' || e.key === 'C' || e.key === ' ') {
      if (!note1.trim()) return;
      e.preventDefault();
      setNote2(note1);
      submitWith(note1, note1);
    }
  };

  if (loadingEtudiants) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
      {/* Header condensé (fix 2026-08-27) */}
      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap text-sm text-gray-700 dark:text-gray-300">
          <span className="font-semibold text-gray-900 dark:text-gray-100">Saisie individuelle</span>
          <span className="text-gray-400">·</span>
          <span>{classe}</span>
          <span className="text-gray-400">·</span>
          <span>{semestre}</span>
          <span className="text-xs text-gray-500 ml-1">
            <kbd className="px-1 py-0.5 bg-amber-100 dark:bg-amber-700 text-amber-900 dark:text-amber-100 rounded font-mono">C</kbd>
            {' '}ou{' '}
            <kbd className="px-1 py-0.5 bg-amber-100 dark:bg-amber-700 text-amber-900 dark:text-amber-100 rounded font-mono">Espace</kbd>
            {' '}= copie D1→D2 + valide + suivant
          </span>
        </div>
        <button
          onClick={() => setShowHistorique(true)}
          className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
          title="Historique des modifications pour cette matière/classe/semestre"
        >
          <Clock size={12} /> Historique
        </button>
      </div>

      {message && (
        <div
          className={`mx-6 mt-4 px-4 py-3 rounded ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Sélection de l'étudiant */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Étudiant <span className="text-red-500">*</span>
          </label>
          <select
            value={etudiantId || ''}
            onChange={(e) => setEtudiantId(e.target.value ? parseInt(e.target.value) : null)}
            required
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">-- Sélectionner un étudiant --</option>
            {etudiants?.map((etudiant) => (
              <option key={etudiant.id} value={etudiant.id}>
                {etudiant.matricule} - {etudiant.nom} {etudiant.prenoms}
              </option>
            ))}
          </select>
        </div>

        {/* Note D.1 + D.2 (fix 2026-08-27 : plus de case absent, vide = 0) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Devoir 1 (D.1)</h4>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Note sur 20 <span className="text-xs text-gray-500 font-normal">— vide = 0</span>
            </label>
            <input
              ref={note1Ref}
              type="number"
              step="0.01"
              min="0"
              max="20"
              value={note1}
              onChange={(e) => setNote1(e.target.value)}
              onBlur={(e) => { if (e.target.value.trim() === '') setNote1('0'); }}
              onKeyDown={handleNoteKeyDown}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: 15.50"
            />
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">Devoir 2 (D.2)</h4>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Note sur 20 <span className="text-xs text-gray-500 font-normal">— vide = 0</span>
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="20"
              value={note2}
              onChange={(e) => setNote2(e.target.value)}
              onBlur={(e) => { if (e.target.value.trim() === '') setNote2('0'); }}
              onKeyDown={handleNoteKeyDown}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Ex: 12.00"
            />
          </div>
        </div>

        {/* Moyenne calculée */}
        {moyenne !== null && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-300">Moyenne des devoirs :</span>
              <span className="text-2xl font-bold text-blue-900 dark:text-blue-200">{moyenne.toFixed(2)} / 20</span>
            </div>
          </div>
        )}

        {/* Boutons */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={() => {
              setEtudiantId(null);
              setNote1('');
              setNote2('');
              setMessage(null);
            }}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Réinitialiser
          </button>
          <button
            type="submit"
            disabled={isSaving || !etudiantId}
            className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </form>

      {/* Modale historique (fix 2026-08-27) */}
      <HistoriqueSaisieModal
        open={showHistorique}
        onClose={() => setShowHistorique(false)}
        matiereId={matiereId}
        matiereLabel={matiereLabel}
        classe={classe}
        semestre={semestre}
        anneeAcademique={anneeAcademique}
      />
    </div>
  );
};

export default SaisieIndividuelle;
